import { defineStyleConfig, SystemStyleObject } from '@chakra-ui/react';
import {
  getTypographyToken,
  ColorMode,
  getBoxSizingStyles,
} from '@allergan-data-labs/alle-elements-core';

import { BadgeColorScheme, BadgeVariants } from './badge';

type VariantNames =
  | 'solidAlert'
  | 'solidInfo'
  | 'solidSuccess'
  | 'solidWarning'
  | 'outlineAlert'
  | 'outlineInfo'
  | 'outlineSuccess'
  | 'outlineWarning'
  | 'ghostAlert'
  | 'ghostInfo'
  | 'ghostSuccess'
  | 'ghostWarning';

type VariantColorSchemes = 'Alert' | 'Info' | 'Success' | 'Warning';

interface VariantAttributes {
  backgroundColor: string;
  borderColor: string;
  colorMode: ColorMode;
  textColor: string;
}

const createVariantStyles = ({
  backgroundColor,
  borderColor,
  textColor,
}: VariantAttributes): SystemStyleObject => ({
  background: backgroundColor,
  borderColor,
  color: textColor,
});

export const getVariantName = (
  variant: BadgeVariants,
  colorScheme: BadgeColorScheme
): VariantNames => {
  const variantColorScheme: VariantColorSchemes = (colorScheme
    .charAt(0)
    .toUpperCase() + colorScheme.slice(1)) as VariantColorSchemes;
  return `${variant}${variantColorScheme}` as VariantNames;
};

export const getVariantStyles = (
  variant: BadgeVariants,
  colorScheme: BadgeColorScheme,
  colorMode: ColorMode
): SystemStyleObject => {
  const variantName = getVariantName(variant, colorScheme);
  const variantMap: { [key in VariantNames]: SystemStyleObject } = {
    outlineAlert: createVariantStyles({
      backgroundColor: `${colorMode}.Container/Brand/Transparent interactive/Enable`,
      borderColor: `${colorMode}.Border/Status/Error medium 3`,
      colorMode,
      textColor: `${colorMode}.Text/Neutral/Default`,
    }),
    outlineInfo: createVariantStyles({
      backgroundColor: `${colorMode}.Container/Brand/Transparent interactive/Enable`,
      borderColor: `${colorMode}.Border/Neutral/Subtle 2`,
      colorMode,
      textColor: `${colorMode}.Text/Neutral/Default`,
    }),
    outlineSuccess: createVariantStyles({
      backgroundColor: `${colorMode}.Container/Brand/Transparent interactive/Enable`,
      borderColor: `${colorMode}.Border/Status/Positive medium 3`,
      colorMode,
      textColor: `${colorMode}.Text/Neutral/Default`,
    }),
    outlineWarning: createVariantStyles({
      backgroundColor: `${colorMode}.Container/Brand/Transparent interactive/Enable`,
      borderColor: `${colorMode}.Border/Status/Warning medium 3`,
      colorMode,
      textColor: `${colorMode}.Text/Neutral/Default`,
    }),
    solidAlert: createVariantStyles({
      backgroundColor: `${colorMode}.Container/Status/Error subtle 1`,
      borderColor: `Transparent`,
      colorMode,
      textColor: `${colorMode}.Text/Status/Error bold 2`,
    }),
    solidInfo: createVariantStyles({
      backgroundColor: `${colorMode}.Container/Neutral/Mute 2`,
      borderColor: `Transparent`,
      colorMode,
      textColor: `${colorMode}.Text/Neutral/Default`,
    }),
    solidSuccess: createVariantStyles({
      backgroundColor: `${colorMode}.Container/Status/Success subtle 1`,
      borderColor: `Transparent`,
      colorMode,
      textColor: `${colorMode}.Text/Status/Success bold 2`,
    }),
    solidWarning: createVariantStyles({
      backgroundColor: `${colorMode}.Container/Status/Warning subtle 1`,
      borderColor: `Transparent`,
      colorMode,
      textColor: `${colorMode}.Text/Status/Warning bold 2`,
    }),
    ghostAlert: createVariantStyles({
      backgroundColor: `Transparent`,
      borderColor: `Transparent`,
      colorMode,
      textColor: `${colorMode}.Text/Status/Error medium 3`,
    }),
    ghostInfo: createVariantStyles({
      backgroundColor: `Transparent`,
      borderColor: `Transparent`,
      colorMode,
      textColor: `${colorMode}.Text/Neutral/Default`,
    }),
    ghostSuccess: createVariantStyles({
      backgroundColor: `Transparent`,
      borderColor: `Transparent`,
      colorMode,
      textColor: `${colorMode}.Text/Status/Positive medium 3`,
    }),
    ghostWarning: createVariantStyles({
      backgroundColor: `Transparent`,
      borderColor: `Transparent`,
      colorMode,
      textColor: `${colorMode}.Text/Status/Warning medium 3`,
    }),
  };

  return variantMap[variantName] || variantMap.outlineInfo;
};

const baseStyle = (): SystemStyleObject => ({
  ...getBoxSizingStyles(),
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',
  h: 24,
  maxW: 200,
  borderWidth: 1,
  borderStyle: 'solid',
  borderRadius: 'Small',
  px: 8,
  py: '0.1875rem',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  ...getTypographyToken('Body/Extra small/Regular'),
});

export const getBadgeIconStyle = (): SystemStyleObject => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  mr: 6,
  '& svg': {
    height: 16,
    width: 16,
  },
});

export const BadgeTheme = defineStyleConfig({
  baseStyle,
});
